/* eslint class-methods-use-this: 0 */
import { XhrConsts } from 'client/util/Constants';
import axios from 'axios';
import get from 'lodash/get';

const { xCSRFToken, xOrgIdHeader } = XhrConsts;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * The base Api class provides common methods that are used by all Api
 * subclasses for both RESTful and non-RESTful endpoints. It provides an
 * initialize method that is used to ensure that the CSRF token and OrgId are
 * present on all requests as well methods to handle errors and stringify an
 * object of query string parameters for use in a GET request.
 */
export default class Api {
  constructor(csrfUrl = '/csrf', csrfHeader = xCSRFToken, orgIdHeader = xOrgIdHeader) {
    // TODO: We will eventually want to have all Api's using `this.axios`
    // rather than the global `axios` to avoid sharing headers.
    // For now, this local axios instance is only being used in LoginApi.
    this.axios = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.csrfUrl = csrfUrl;
    this.csrfHeader = csrfHeader;
    this.orgIdHeader = orgIdHeader;
  }

  initialize() {
    return new Promise((resolve, reject) => {
      // Ensure that the X-CSRF-Token is passed in the headers for all requests
      // making a call to the server side to obtain the token if not yet set.
      if (axios.defaults.headers.common[this.csrfHeader]) {
        resolve();

        return;
      }

      axios.get(this.csrfUrl)
        .then((resp) => {
          axios.defaults.headers.common[this.csrfHeader] = resp.data;
          resolve();
        })
        .catch((error) => {
          this.handleError(reject, 'Could not get CSRF token!', error);
        });
    });
  }

  handleError(reject, mesg, error) {
    if (axios.isCancel(error)) {
      reject(error);
    }

    if (typeof reject === 'function') {
      const apiError = new Error();
      const responseMessage = get(error, 'response.data.message');
      const errorMesg = get(error, 'response.data.error');
      apiError.message = responseMessage || errorMesg || mesg;
      apiError.originalError = error;

      reject(apiError);
    }
    return Error(mesg);
  }

  /**
   * Stringify an object of query string parameters for use in a GET request.
   * @see
   * https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object
   * http://api.jquery.com/jquery.param/
   */
  param(obj, prefix) {
    const str = [];

    Object.keys(obj).forEach((property) => {
      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        const key = prefix ? `${prefix}[${property}]` : property;
        const value = obj[property];

        if (value !== null && typeof value === 'object') {
          // Recurse to process nested objects
          str.push(this.param(value, key));
        } else {
          const typeOfValue = typeof value;

          // Add the key/value pair to the URL if the value is a boolean, number or non-empty string
          if (typeOfValue === 'boolean'
              || typeOfValue === 'number'
              || (typeOfValue === 'string' && value.trim().length !== 0)) {
            str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          }
        }
      }
    });

    return str.join('&');
  }
}
